import { useCallback, useState } from 'react'
import type { DragEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Columns3CogIcon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
  RotateCcwIcon,
} from 'lucide-react'

import { RundownPermission } from '~shared/types'

import { useUser } from '@/entities/user/index.ts'

import { useRundownPermission } from '../../permissions/lib/use-rundown-permission.ts'
import { GRID_DOM_HEADER } from '../grid/grid.const.ts'

import { columnLayoutActions } from './column-layout.ts'
import { columnActions, useIsAddingColumn } from './column.actions.ts'
import { getColumnNameDomId } from './column.const.ts'
import { columnsDerived, useColumnsDerived } from './column.derived.ts'
import { ColumnHeader } from './ColumnHeader.tsx'

export function ColumnHeaders() {
  // Render reads only ids (and the names of hidden columns). Valtio tracks
  // what is read, so a rename re-renders just that column's header. Taking a
  // list without reading into it, eg. only for a memo or a callback, would
  // subscribe to every field of every column.
  const { visibleColumns } = useColumnsDerived()

  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null)
  const [draggedToPosition, setDraggedToPosition] = useState<number | null>(
    null,
  )
  const isEditor = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  const onDragStart = useCallback((columnId: string) => {
    setDraggedColumnId(columnId)
  }, [])

  const onDragEnd = useCallback(() => {
    setDraggedColumnId(null)
    setDraggedToPosition(null)
  }, [])

  const onDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>, columnId: string) => {
      if (!draggedColumnId) return

      event.preventDefault() // Prevent default drag behavior

      event.dataTransfer.dropEffect = 'move' // Set correct drag cursor

      const targetElement = event.currentTarget
      const elementWidth = targetElement.offsetWidth
      const mouseX = event.clientX
      const elementMidpoint =
        targetElement.getBoundingClientRect().left + elementWidth / 2

      const targetIndex = columnsDerived.visibleColumns.findIndex(
        (c) => c.id === columnId,
      )
      const newIndex = mouseX > elementMidpoint ? targetIndex + 1 : targetIndex

      // Same value is a no-op, React skips the re-render
      setDraggedToPosition(newIndex)
    },
    [draggedColumnId],
  )

  function onDrop() {
    if (draggedColumnId !== null && draggedToPosition !== null) {
      columnActions.moveToVisibleIndex(draggedColumnId, draggedToPosition)
    }
    setDraggedColumnId(null)
    setDraggedToPosition(null)
  }

  return (
    <div
      role="rowgroup"
      id={GRID_DOM_HEADER}
      className="relative flex gap-3"
    >
      <div
        role="row"
        aria-label="Rundown grid columns"
        className="relative flex gap-1"
        onDrop={onDrop}
      >
        {visibleColumns.map((column, index) => {
          return (
            <div
              key={column.id}
              role="columnheader"
              aria-labelledby={getColumnNameDomId(column.id)}
              className="relative"
            >
              {/* On reorder, render the vertical bar if it's before this column */}
              {draggedColumnId !== null && draggedToPosition === index && (
                <div className="absolute h-full w-1 bg-blue-500/80 -left-1 rounded-full" />
              )}

              <ColumnHeader
                columnId={column.id}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                isDragging={draggedColumnId !== null}
              />
            </div>
          )
        })}

        {/* On reorder, render the vertical bar at the end if dragging beyond the last column */}
        {draggedColumnId !== null &&
          draggedToPosition === visibleColumns.length && (
            <div className="absolute h-full w-1 bg-blue-500/80 right-0 rounded-full" />
          )}
      </div>

      <ColumnsMenu isEditor={isEditor} />
    </div>
  )
}

/**
 * The "Columns" actions menu at the end of the header row.
 */
function ColumnsMenu({ isEditor }: { isEditor: boolean }) {
  const isPending = useIsAddingColumn()
  const { userHiddenColumns } = useColumnsDerived()
  const navigate = useNavigate()
  const { data: user } = useUser()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="secondary"
            size="sm"
          >
            <Columns3CogIcon data-icon="inline-start" />
            Columns
          </Button>
        }
      />

      <DropdownMenuContent
        align="end"
        className="w-auto"
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => columnActions.add()}
            disabled={!isEditor || isPending}
          >
            <PlusIcon />
            Add column
          </DropdownMenuItem>

          {!user?.uid && (
            <DropdownMenuItem
              onClick={() =>
                navigate(
                  `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                )
              }
            >
              <PlusIcon />
              Add private notes
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <EyeOffIcon />
            Hidden columns
          </DropdownMenuSubTrigger>

          <DropdownMenuSubContent>
            {userHiddenColumns.length === 0 && (
              <DropdownMenuItem disabled>No hidden columns</DropdownMenuItem>
            )}

            {userHiddenColumns.map((c) => (
              <DropdownMenuItem
                key={c.id}
                onClick={() => columnLayoutActions.showColumn(c.id)}
              >
                {c.name || 'Untitled'}
              </DropdownMenuItem>
            ))}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={columnLayoutActions.showAll}
              disabled={userHiddenColumns.length === 0}
            >
              <EyeIcon />
              Show all
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={columnLayoutActions.reset}>
          <RotateCcwIcon />
          Reset columns layout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
